import React, { useEffect, useState } from "react";
import { IoIosClose } from "react-icons/io";
import { toast } from "react-toastify";
import RequestService from "../../services/RequestService";
import MotivoJustificativa from "../../enums/MotivoJustificativa";
import ResponsavelJustificativa from "../../enums/ResponsavelJustificativa";
import EstoqueCodSapPanel from "./EstoqueCodSapPanel";

/**
 * Modal de validação da justificativa (lado Vivo).
 *
 * Mostra a justificativa da Amerinode (somente leitura) e permite a Vivo
 * Abonar (aprovar), Recusar (cabe uma réplica da Amerinode) ou marcar como
 * Não aceito (recusa definitiva já na 1ª avaliação, sem passar pela réplica),
 * registrando uma observação. A observação é obrigatória nas duas recusas.
 *
 * Props:
 *  - isOpen (bool)
 *  - setIsOpen (fn)
 *  - request (object)  — Request alvo (_id, idRequest, justificativa, justificativaInfo, tipoAtendimento, drs*)
 *  - onSaved (fn)      — callback após avaliar com sucesso (recarrega a lista)
 */
export default function ValidarJustificativaModal({ isOpen, setIsOpen, request, onSaved }) {
    const [observacao, setObservacao] = useState("");
    const [savingDecisao, setSavingDecisao] = useState(null); // 'ABONAR' | 'RECUSAR' | 'NAO_ACEITAR' | null

    useEffect(() => {
        if (isOpen) setObservacao(request?.justificativaInfo?.observacaoVivo || "");
    }, [isOpen, request]);

    const handleClose = () => {
        setIsOpen(false);
        setObservacao("");
        setSavingDecisao(null);
    };

    const motivoLabel = (cod) => {
        const m = MotivoJustificativa.find((x) => x.cod === cod);
        return m ? m.descricao : (cod || "—");
    };

    const responsavelLabel = (cod) => {
        const r = ResponsavelJustificativa.find((x) => x.cod === cod);
        return r ? r.descricao : (cod || "—");
    };

    const faixaLabel = (cod) => {
        if (!cod) return "—";
        return cod === "99" ? "Dentro de Área (99)" : `Faixa ${cod}`;
    };

    const areaSla = (() => {
        const a = request?.drsAtender?._id || request?.drsAtender;
        const o = request?.drsOriginal?._id || request?.drsOriginal;
        if (!a || !o) return null;
        return String(a) === String(o) ? "Dentro Área" : "Fora Área";
    })();

    const handleAvaliar = async (decisao) => {
        const ehRecusa = decisao === "RECUSAR" || decisao === "NAO_ACEITAR";

        if (ehRecusa && !observacao.trim()) {
            toast.error("A observação é obrigatória para recusar.");
            return;
        }

        // "Não aceito" encerra o chamado sem dar direito a réplica — confirma
        // antes, já que não há como voltar atrás pela tela.
        if (decisao === "NAO_ACEITAR" && !window.confirm(
            "Marcar esta justificativa como NÃO ACEITO?\n\nA recusa é definitiva: a Amerinode não poderá replicar."
        )) {
            return;
        }

        setSavingDecisao(decisao);
        try {
            const resp = await RequestService.avaliarJustificativa(request._id, {
                decisao,
                observacaoVivo: observacao,
            });
            toast.success(resp?.message || "Avaliação registrada!");
            if (onSaved) onSaved();
            handleClose();
        } catch (error) {
            toast.error(error?.message || "Erro ao registrar avaliação.");
        }
        setSavingDecisao(null);
    };

    if (!isOpen) return null;

    const saving = savingDecisao !== null;

    return (
        <div
            className="fixed inset-0 z-50 flex justify-center items-center bg-black bg-opacity-50"
            onClick={handleClose}
        >
            <div
                className="relative bg-white rounded-lg shadow-lg w-full max-w-xl mx-4"
                onClick={(e) => e.stopPropagation()}
            >
                <button
                    onClick={handleClose}
                    className="absolute -top-7 -right-7 rounded-full p-1"
                >
                    <div className="w-12 h-12 flex bg-gray-200 justify-center items-center rounded-full">
                        <IoIosClose color="black" size={32} />
                    </div>
                </button>

                <div className="flex flex-col px-4 pt-4 pb-3 gap-1 bg-gray-800 text-white rounded-t-lg">
                    <div className="text-xl font-bold">
                        Validação — # {request?.idRequest}
                    </div>
                    <div className="text-xs text-gray-300">
                        {request?.tipoAtendimento || "—"}{areaSla ? ` • ${areaSla}` : ""}
                    </div>
                </div>

                <div className="flex flex-col gap-3 p-4 text-sm">
                    <div className="text-purple-700 font-semibold">Validação Vivo</div>
                    <div className="text-xs text-gray-500 -mt-2">
                        Analise a justificativa apresentada e registre seu parecer.
                    </div>

                    {/* Justificativa da Amerinode (somente leitura) */}
                    <div className="flex flex-col gap-1 p-3 rounded-md border border-zinc-200 bg-gray-50">
                        <span className="text-[11px] font-bold text-purple-700 uppercase">Justificativa Amerinode</span>
                        <span className="text-gray-800 whitespace-pre-wrap">
                            {request?.justificativa?.trim() || "— sem comentário —"}
                        </span>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <div className="flex flex-col gap-1 p-3 rounded-md border border-zinc-200 bg-gray-50">
                            <span className="text-[11px] font-bold text-purple-700 uppercase">Motivo</span>
                            <span className="text-gray-800">{motivoLabel(request?.justificativaInfo?.motivo)}</span>
                        </div>
                        <div className="flex flex-col gap-1 p-3 rounded-md border border-zinc-200 bg-gray-50">
                            <span className="text-[11px] font-bold text-purple-700 uppercase">Responsável</span>
                            <span className="text-gray-800">{responsavelLabel(request?.justificativaInfo?.responsavel)}</span>
                        </div>
                        <div className="flex flex-col gap-1 p-3 rounded-md border border-zinc-200 bg-gray-50">
                            <span className="text-[11px] font-bold text-purple-700 uppercase">Faixa</span>
                            <span className="text-gray-800">{faixaLabel(request?.faixa_final || request?.faixa)}</span>
                            {/* Só destaca a original quando a final foi alterada manualmente. */}
                            {request?.faixa_final && request?.faixa && request.faixa_final !== request.faixa && (
                                <span className="text-[10px] text-amber-700">
                                    Original: {faixaLabel(request.faixa)} (alterada)
                                </span>
                            )}
                        </div>
                    </div>

                    {/* Estoque do código SAP: movimentações 36m, saldo do dia e faixa ideal.
                        Componente compartilhado com o JustificarModal. */}
                    <EstoqueCodSapPanel request={request} />

                    {request?.justificativaInfo?.somenteFaturamento && (
                        <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                            <strong>Somente Faturamento</strong> — marcado pela Amerinode (não contabilizado no SLA).
                        </div>
                    )}

                    {/* Observação Vivo */}
                    <div className="flex flex-col gap-1">
                        <label className="font-semibold text-gray-700">
                            Observação Vivo <span className="text-gray-400 font-normal">(obrigatória para recusar ou não aceitar)</span>:
                        </label>
                        <textarea
                            value={observacao}
                            onChange={(e) => setObservacao(e.target.value)}
                            rows={4}
                            placeholder="Registre a observação sobre este chamado."
                            className="w-full rounded-md px-3 py-2 text-gray-900 shadow-sm placeholder:text-gray-400 border border-zinc-400 outline-none ring-0 resize-none"
                        />
                    </div>

                    <div className="flex justify-end gap-2 mt-2 flex-wrap">
                        <button
                            onClick={handleClose}
                            disabled={saving}
                            className="bg-zinc-500 hover:bg-zinc-600 text-white px-4 py-1 rounded-2xl disabled:opacity-50"
                        >
                            Cancelar
                        </button>
                        {/* Recusa definitiva — não abre réplica para a Amerinode. */}
                        <button
                            onClick={() => handleAvaliar("NAO_ACEITAR")}
                            disabled={saving}
                            title="Recusa definitiva — a Amerinode não poderá replicar"
                            className="bg-red-900 hover:bg-red-950 text-white px-4 py-1 rounded-2xl disabled:opacity-50"
                        >
                            {savingDecisao === "NAO_ACEITAR" ? "..." : "Não aceito"}
                        </button>
                        <button
                            onClick={() => handleAvaliar("RECUSAR")}
                            disabled={saving}
                            title="Recusa com direito a uma réplica da Amerinode"
                            className="bg-red-600 hover:bg-red-700 text-white px-4 py-1 rounded-2xl disabled:opacity-50"
                        >
                            {savingDecisao === "RECUSAR" ? "..." : "Recusar"}
                        </button>
                        <button
                            onClick={() => handleAvaliar("ABONAR")}
                            disabled={saving}
                            className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-1 rounded-2xl disabled:opacity-50"
                        >
                            {savingDecisao === "ABONAR" ? "..." : "Abonar"}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
